import type {
	AssociatedPrioritySchemesPerPriority,
	ExpandResponsePriorityScheme,
	Priority,
	SearchPrioritiesAPIResponse,
} from '../../common/types';

export const refineSearchPriorityAPIResponse = (obj: unknown): SearchPrioritiesAPIResponse => {
	if (
		!obj ||
		typeof obj !== 'object' ||
		!('isLast' in obj) ||
		typeof obj.isLast !== 'boolean' ||
		!('maxResults' in obj) ||
		typeof obj.maxResults !== 'number' ||
		!('startAt' in obj) ||
		typeof obj.startAt !== 'number' ||
		!('total' in obj) ||
		typeof obj.total !== 'number' ||
		!('values' in obj) ||
		!Array.isArray(obj.values)
	) {
		throw new Error('Unexpected shape of SearchPrioritiesAPIResponse');
	}

	return {
		isLast: obj.isLast,
		maxResults: obj.maxResults,
		startAt: obj.startAt,
		total: obj.total,
		values: obj.values.map(refinePriorityResponseObject),
	};
};

export const refinePriorityResponseObject = (obj: unknown): Priority => {
	if (
		!obj ||
		typeof obj !== 'object' ||
		!('statusColor' in obj) ||
		typeof obj.statusColor !== 'string' ||
		!('iconUrl' in obj) ||
		typeof obj.iconUrl !== 'string' ||
		!('name' in obj) ||
		typeof obj.name !== 'string' ||
		!('id' in obj) ||
		typeof obj.id !== 'string' ||
		!('isDefault' in obj) ||
		typeof obj.isDefault !== 'boolean' ||
		!('schemes' in obj) ||
		typeof obj.schemes !== 'object'
	) {
		throw new Error('Unexpected shape of Priority');
	}

	let refinedPriority: Priority = {
		statusColor: obj.statusColor,
		iconUrl: obj.iconUrl,
		name: obj.name,
		id: obj.id,
		isDefault: obj.isDefault,
		schemes: refineAssociatedPrioritySchemesPerPriority(obj.schemes),
	};

	if ('description' in obj && typeof obj.description === 'string') {
		refinedPriority = {
			...refinedPriority,
			description: obj.description,
		};
	}

	if ('avatarId' in obj && typeof obj.avatarId === 'number') {
		refinedPriority = {
			...refinedPriority,
			avatarId: String(obj.avatarId),
		};
	}

	return refinedPriority;
};

export const refineAssociatedPrioritySchemesPerPriority = (
	obj: unknown,
): AssociatedPrioritySchemesPerPriority => {
	if (
		!obj ||
		typeof obj !== 'object' ||
		!('isLast' in obj) ||
		typeof obj.isLast !== 'boolean' ||
		!('maxResults' in obj) ||
		typeof obj.maxResults !== 'number' ||
		!('startAt' in obj) ||
		typeof obj.startAt !== 'number' ||
		!('total' in obj) ||
		typeof obj.total !== 'number' ||
		!('values' in obj) ||
		!Array.isArray(obj.values)
	) {
		throw new Error('Unexpected shape of AssociatedPrioritySchemesPerPriority');
	}

	const refinedPrioritySchemes: Array<ExpandResponsePriorityScheme> = obj.values.map(
		refineExpandResponsePrioritySchemeObject,
	);

	return {
		isLast: obj.isLast,
		maxResults: obj.maxResults,
		startAt: obj.startAt,
		total: obj.total,
		values: refinedPrioritySchemes,
	};
};

export const refineExpandResponsePrioritySchemeObject = (
	obj: unknown,
): ExpandResponsePriorityScheme => {
	if (
		!obj ||
		typeof obj !== 'object' ||
		!('id' in obj) ||
		typeof obj.id !== 'string' ||
		!('name' in obj) ||
		typeof obj.name !== 'string' ||
		!('self' in obj) ||
		typeof obj.self !== 'string'
	) {
		throw new Error('Unexpected shape of ExpandResponsePriorityScheme');
	}

	return {
		id: obj.id,
		name: obj.name,
		self: obj.self,
	};
};
